import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { Divider, Grid, Typography } from '@mui/material';
import AutocompleteWithFetch from 'components/Inputs/AutoCompletewithFetch';
import { ConfirmationPopup } from 'components/PopUp';
import DataGridTable from 'components/Tables';
import {
  REMOVE_EMPLOYEE_MUTATION,
  RemoveEmployeeResponse,
  RemoveEmployeeVariables,
} from 'graphql/mutation/user';
import {
  GET_REPORTEES_QUERY,
  GetReporteesResponse,
  GetReporteesVariables,
  GetUsersQueryResponse,
  GetUsersQueryVariables,
} from 'graphql/query/user';
import { useState } from 'react';
import { toast } from 'react-toastify';
import extendedTheme from 'styles/theme';

const GET_USERS_QUERY = gql`
  query GetUsers($filter: UserFilter) {
    getUsers(filter: $filter) {
      _id
      name
      empId
    }
  }
`;

const RemoveEmployeeForm = () => {
  const [formState, setFormState] = useState<Record<string, any>>({});
  const [openPopUp, togglePopUp] = useState(false);

  const [getUsers, { loading: loadingUsers, data: users }] = useLazyQuery<
    GetUsersQueryResponse,
    GetUsersQueryVariables
  >(GET_USERS_QUERY, {
    fetchPolicy: 'network-only',
  });

  const [getReportees, { loading: loadingReportees, data: reportees }] = useLazyQuery<
    GetReporteesResponse,
    GetReporteesVariables
  >(GET_REPORTEES_QUERY, {
    fetchPolicy: 'network-only',
  });

  const [removeEmployee, { loading: removingEmployee }] = useMutation<
    RemoveEmployeeResponse,
    RemoveEmployeeVariables
  >(REMOVE_EMPLOYEE_MUTATION);

  const handleChange = (fieldName: string, value: any) => {
    setFormState(prev => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleEmployeeChange = (value: any) => {
    setFormState(prev => ({
      ...prev,
      removeEmployee: value,
      reassignReportee: { _id: '', name: '' },
    }));

    if (!!value._id) {
      getReportees({
        variables: {
          userId: value._id,
        },
      });
    }
  };

  const handleSubmit = () => {
    removeEmployee({
      variables: {
        user: formState.removeEmployee._id,
        reasignee: !!formState.reassignReportee?._id ? formState.reassignReportee?._id : undefined,
      },
      onCompleted: () => {
        togglePopUp(false);
        setFormState({});
        toast.success('Employee Removed Successfully');
      },
    });
  };

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        togglePopUp(true);
      }}
    >
      <Grid container item xs={12} md={10} lg={8} xl={6} rowGap={2} columnSpacing={1.5} mb={3}>
        <Grid item xs={12}>
          <AutocompleteWithFetch
            fetch={getUsers}
            handleChange={handleEmployeeChange}
            label="Employee to remove"
            value={formState.removeEmployee ?? { _id: '', name: '' }}
            loading={loadingUsers}
            options={users?.getUsers ?? []}
            required
          />
        </Grid>

        {!!formState?.removeEmployee?._id &&
          !loadingReportees &&
          !!reportees?.getReportees?.length && (
            <Grid item xs={12} container>
              <Grid item xs={12}>
                <AutocompleteWithFetch
                  fetch={getUsers}
                  handleChange={val => handleChange('reassignReportee', val)}
                  label="Re-Assign Reportees"
                  loading={loadingUsers}
                  options={
                    users?.getUsers.filter(usr => {
                      return formState.removeEmployee._id !== usr._id;
                    }) ?? []
                  }
                  value={formState.reassignReportee}
                  required
                />
              </Grid>
              <Grid item xs={12} my={1}>
                <Divider variant="middle">
                  <Typography
                    variant="subtitle1"
                    letterSpacing={1}
                    fontWeight={600}
                    color={extendedTheme.palette.primary.main}
                  >
                    Reportees under {formState.removeEmployee?.name}
                  </Typography>
                </Divider>
              </Grid>

              <Grid item xs={12}>
                <DataGridTable
                  getRowId={row => row._id}
                  columns={[
                    {
                      field: 'empId',
                      headerName: 'ID',
                      minWidth: 100,
                    },
                    {
                      field: 'name',
                      headerName: 'Name',
                      minWidth: 150,
                      flex: 1,
                    },
                    {
                      field: 'department',
                      headerName: 'Department',
                      minWidth: 150,
                    },
                    {
                      field: 'designation',
                      headerName: 'Designation',
                      minWidth: 150,
                    },
                  ]}
                  sx={{
                    maxHeight: '50vh',
                    maxWidth: '80vw',
                  }}
                  rows={reportees?.getReportees ?? []}
                />
              </Grid>
            </Grid>
          )}

        <ConfirmationPopup
          loading={loadingReportees || removingEmployee}
          title="Are you sure you want to remove this Employee?"
          onConfirmation={handleSubmit}
          onClose={() => togglePopUp(false)}
          open={openPopUp}
        />

        <Grid item xs={12} container justifyContent="flex-end">
          <LoadingButton variant="contained" type="submit">
            Remove
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  );
};

export default RemoveEmployeeForm;
